import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { DragEndEvent } from "@dnd-kit/core";
import { ReactNode } from "react";
import { LayerDragContext } from "./LayerDragContext";
import { useTenantTranslation } from "hooks/formatters";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { MaterialRead } from "src/store/api/generatedApi";
import { BasketColumn } from "./BasketColumn";
import { LayerEditorSize } from "./LayerBar";
import { MaterialBalanceBar } from "./MaterialBalanceBar";
import { MaterialBalance, MixLayerState } from "./types";

interface Props {
  mix: MixLayerState;
  materials: MaterialRead[];
  onWeightChange: (layerId: string, weight: number) => void;
  onDelete: (layerId: string) => void;
  onAddLayer: (
    basketNumber: number,
    materialName: string,
    weight: number
  ) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onSplitRequest: (layerId: string) => void;
  weightsEditable: boolean;
  massScaleFactor: number;
  balances: MaterialBalance[];
  showSteelGrades: boolean;
  size: LayerEditorSize;
  manuallyLayered: boolean;
  onResetMixLayers: (() => void) | undefined;
  collapsed: boolean;
  onToggleCollapse: (() => void) | undefined;
  summary?: ReactNode;
}

export const MixLayerPanel = ({
  mix,
  materials,
  onWeightChange,
  onDelete,
  onAddLayer,
  onDragEnd,
  onSplitRequest,
  weightsEditable,
  massScaleFactor,
  balances,
  showSteelGrades,
  size,
  manuallyLayered,
  onResetMixLayers,
  collapsed,
  onToggleCollapse,
  summary,
}: Props) => {
  const { t } = useTenantTranslation();
  const productionGroupNameEnabled = useIsBehindFeatureFlag(
    "layering_production_group_name"
  );
  const hasImbalancedBalances = balances.some(
    (balance) => !balance.isWithinTolerance
  );
  const showProductionGroupName =
    productionGroupNameEnabled && mix.productionGroupName.length > 0;
  const showInlineChips =
    (showSteelGrades && mix.steelGradeProduction.length > 0) ||
    manuallyLayered ||
    hasImbalancedBalances ||
    showProductionGroupName;

  return (
    <Stack spacing={1} data-testid={`mix-panel-${mix.mixId}`}>
      {showInlineChips || onToggleCollapse || summary ? (
        <Stack
          direction="row"
          spacing={0.5}
          flexWrap="wrap"
          alignItems="center"
        >
          {onToggleCollapse ? (
            <Tooltip title={collapsed ? t("showDetails") : t("hideDetails")}>
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                aria-label={collapsed ? t("showDetails") : t("hideDetails")}
              >
                {collapsed ? (
                  <KeyboardArrowRightIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          ) : null}
          {showProductionGroupName && (
            <Typography
              variant="subtitle1"
              fontWeight="fontWeightBold"
              sx={{ pr: 0.5 }}
            >
              {mix.productionGroupName}
            </Typography>
          )}
          {showSteelGrades &&
            mix.steelGradeProduction.map((sgp) => (
              <Chip
                key={sgp.steel_grade_name}
                label={sgp.steel_grade_name}
                size="small"
              />
            ))}
          {summary}
          {manuallyLayered ? (
            <>
              <Chip label={t("manuallyLayered")} size="small" color="warning" />
              {onResetMixLayers ? (
                <Tooltip title={t("resetToAutoLayering")}>
                  <IconButton size="small" onClick={onResetMixLayers}>
                    <RestartAltIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </>
          ) : null}
          <MaterialBalanceBar
            balances={balances}
            massScaleFactor={massScaleFactor}
          />
        </Stack>
      ) : null}
      {collapsed ? null : (
        <LayerDragContext onDragEnd={onDragEnd}>
          <Stack direction="row" spacing={1}>
            {mix.baskets.map((basket) => (
              <BasketColumn
                key={basket.basketNumber}
                basket={basket}
                materials={materials}
                onLayerWeightChange={onWeightChange}
                onLayerDelete={onDelete}
                onLayerSplit={onSplitRequest}
                onAddLayer={onAddLayer}
                weightsEditable={weightsEditable}
                massScaleFactor={massScaleFactor}
                size={size}
              />
            ))}
          </Stack>
        </LayerDragContext>
      )}
    </Stack>
  );
};
